
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useUpdateClass, useUploadClassImage } from '@/api/classes';
import type { GymClass } from '@/api/classes';
import { StaffSelector } from './staff-selector';
import { DaysOfWeekSelector } from './days-of-week-selector';
import { BlackoutDatesManager } from './blackout-dates-manager';
import { Upload, X, Loader2, ImageIcon } from 'lucide-react';

const classSchema = z.object({
  name: z.string().min(1, 'Class name is required'),
  label: z.string().min(1, 'Class label is required'),
  isActive: z.boolean(),
  staffMember: z.string().optional(),
  daysOfWeek: z.array(z.number()).optional(),
});

type ClassFormData = z.infer<typeof classSchema>;

interface EditClassDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymClass: GymClass;
}

export function EditClassDialog({
  open,
  onOpenChange,
  gymClass,
}: EditClassDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const updateClassMutation = useUpdateClass();
  const uploadImageMutation = useUploadClassImage();
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(gymClass.image || null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: t('common.error'), description: 'Image must be under 5MB', variant: 'destructive' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result as string;
      setImagePreview(result);
      setImageBase64(result);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadImage = async () => {
    if (!imageBase64) return;
    try {
      const { url } = await uploadImageMutation.mutateAsync({
        classId: gymClass._id,
        image: imageBase64,
      });
      setImagePreview(url);
      setImageBase64(null);
      toast({ title: t('common.success'), description: 'Class image uploaded' });
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.message || 'Failed to upload image',
        variant: 'destructive',
      });
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ClassFormData>({
    resolver: zodResolver(classSchema),
    defaultValues: {
      name: gymClass.name,
      label: gymClass.label,
      isActive: gymClass.isActive,
      staffMember: gymClass.staffMember || '',
      daysOfWeek: gymClass.daysOfWeek || [],
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: gymClass.name,
        label: gymClass.label,
        isActive: gymClass.isActive,
        staffMember: gymClass.staffMember || '',
        daysOfWeek: gymClass.daysOfWeek || [],
      });
      setImagePreview(gymClass.image || null);
      setImageBase64(null);
    }
  }, [open, gymClass, reset]);

  const staffMember = watch('staffMember');
  const daysOfWeek = watch('daysOfWeek');
  const isActive = watch('isActive');

  const onSubmit = async (data: ClassFormData) => {
    try {
      const payload: any = {
        name: data.name,
        label: data.label,
        isActive: data.isActive,
      };

      if (data.staffMember) {
        payload.staffMember = data.staffMember;
      } else {
        payload.staffMember = null;
      }

      if (data.daysOfWeek && data.daysOfWeek.length > 0) {
        payload.daysOfWeek = data.daysOfWeek;
      } else {
        payload.daysOfWeek = [];
      }

      await updateClassMutation.mutateAsync({
        classId: gymClass._id,
        data: payload,
      });

      toast({
        title: t('common.success'),
        description: t('classes.classUpdated', 'Class updated successfully'),
      });
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.message || t('common.errorOccurred'),
        variant: 'destructive',
      });
    }
  };

  const handleAddBlackoutDate = async (date: string) => {
    try {
      await updateClassMutation.mutateAsync({
        classId: gymClass._id,
        data: { addBlackoutDate: date },
      });
      toast({
        title: t('common.success'),
        description: t('classes.blackoutDateAdded', 'Blackout date added'),
      });
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.message || t('common.errorOccurred'),
        variant: 'destructive',
      });
    }
  };

  const handleRemoveBlackoutDate = async (date: string) => {
    try {
      await updateClassMutation.mutateAsync({
        classId: gymClass._id,
        data: { removeBlackoutDate: date },
      });
      toast({
        title: t('common.success'),
        description: t('classes.blackoutDateRemoved', 'Blackout date removed'),
      });
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.message || t('common.errorOccurred'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('classes.editClass', 'Edit Class')}</DialogTitle>
          <DialogDescription>
            {t('classes.editClassDescription', 'Update class settings and schedule')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Class Image */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              {t('classes.classImage', 'Class Image')}
            </Label>
            <div className="flex items-center gap-4">
              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Class"
                    className="h-20 w-20 rounded-lg object-cover border"
                  />
                  <button
                    type="button"
                    onClick={() => { setImagePreview(null); setImageBase64(null); }}
                    className="absolute -top-2 -right-2 rounded-full bg-destructive p-0.5 text-destructive-foreground hover:bg-destructive/80"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <div
                  className="flex h-20 w-20 items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25 cursor-pointer hover:border-primary/50"
                  onClick={() => imageInputRef.current?.click()}
                >
                  <Upload className="h-5 w-5 text-muted-foreground" />
                </div>
              )}
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => imageInputRef.current?.click()}
                >
                  {imagePreview ? 'Change' : 'Upload'}
                </Button>
                {imageBase64 && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleUploadImage}
                    disabled={uploadImageMutation.isPending}
                  >
                    {uploadImageMutation.isPending ? (
                      <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Saving...</>
                    ) : (
                      'Save Image'
                    )}
                  </Button>
                )}
              </div>
            </div>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageChange}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">{t('classes.className')}</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder={t('classes.className')}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="label">{t('classes.classLabel')}</Label>
            <Input
              id="label"
              {...register('label')}
              placeholder={t('classes.classLabel')}
            />
            {errors.label && (
              <p className="text-sm text-destructive">{errors.label.message}</p>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="isActive"
              checked={isActive}
              onCheckedChange={(checked) => setValue('isActive', checked)}
            />
            <Label htmlFor="isActive" className="cursor-pointer">
              {t('classes.active')}
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="staffMember">
              {t('classes.defaultInstructor', 'Default Instructor')} ({t('common.optional', 'Optional')})
            </Label>
            <StaffSelector
              value={staffMember || ''}
              onChange={(value) => setValue('staffMember', value)}
              allowClear
              placeholder={t('classes.selectInstructor', 'Select instructor...')}
            />
            <p className="text-sm text-muted-foreground">
              {t('classes.defaultInstructorHint', 'This instructor will be assigned to all timeslots by default')}
            </p>
          </div>

          <div className="space-y-2">
            <Label>
              {t('classes.classSchedule', 'Class Schedule')} ({t('common.optional', 'Optional')})
            </Label>
            <DaysOfWeekSelector
              value={daysOfWeek || []}
              onChange={(days) => setValue('daysOfWeek', days)}
            />
            <p className="text-sm text-muted-foreground">
              {t('classes.classScheduleHint', 'If empty, uses timeslot-level settings')}
            </p>
          </div>

          <div className="space-y-2">
            <Label>
              {t('classes.blackoutDates', 'Blackout Dates')}
            </Label>
            <BlackoutDatesManager
              dates={gymClass.blackoutDates || []}
              onAdd={handleAddBlackoutDate}
              onRemove={handleRemoveBlackoutDate}
              disabled={updateClassMutation.isPending}
            />
            <p className="text-sm text-muted-foreground">
              {t('classes.blackoutDatesHint', 'Class will be unavailable on these dates')}
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={updateClassMutation.isPending}>
              {updateClassMutation.isPending
                ? t('common.saving')
                : t('common.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
